import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';

interface TaskTimeData {
  totalSeconds: number;
  estimateHours: number | null;
  lastSessionSeconds: number;
  sessionCount: number;
  taskName: string | null;
  boardName: string | null;
  updatedAt: string | null;
}

interface UseMondayTaskTimesResult {
  // Map of taskId -> time data
  times: Record<string, TaskTimeData>;
  // Loading state
  loading: boolean;
  // Error state
  error: string | null;
  // Fetch times for specific task IDs
  fetchTimes: (taskIds: string[]) => Promise<void>;
  // Add time to a task (incremental)
  addTime: (taskId: string, secondsToAdd: number, metadata?: { taskName?: string; boardName?: string; estimateHours?: number }) => Promise<void>;
  // Get time for a single task (from cache)
  getTime: (taskId: string) => number;
  // Refresh all cached times
  refresh: () => Promise<void>;
}

/**
 * Hook for managing persistent Monday.com task time tracking
 * Fetches and caches cumulative time spent on tasks from the database
 */
export const useMondayTaskTimes = (userId?: string): UseMondayTaskTimesResult => {
  const [times, setTimes] = useState<Record<string, TaskTimeData>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const fetchedTaskIdsRef = useRef<Set<string>>(new Set());

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Fetch times for multiple task IDs
   */
  const fetchTimes = useCallback(async (taskIds: string[]) => {
    if (!taskIds || taskIds.length === 0) return;
    
    // Filter out already fetched task IDs to avoid redundant requests
    const newTaskIds = taskIds.filter(id => !fetchedTaskIdsRef.current.has(id));
    if (newTaskIds.length === 0) return;
    
    if (!isMountedRef.current) return;
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API_URL}/monday/task-times`, {
        taskIds: newTaskIds,
        userId
      }, { withCredentials: true });
      
      if (!isMountedRef.current) return;
      
      const { times: fetchedTimes } = response.data;
      
      // Mark these task IDs as fetched
      newTaskIds.forEach(id => fetchedTaskIdsRef.current.add(id));
      
      // Merge with existing times
      setTimes(prev => ({
        ...prev,
        ...fetchedTimes
      }));
    } catch (err: any) {
      if (!isMountedRef.current) return;
      console.error('Failed to fetch Monday task times:', err);
      setError(err.message || 'Failed to fetch task times');
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [userId]);

  /**
   * Add time to a task (incremental)
   */
  const addTime = useCallback(async (
    taskId: string, 
    secondsToAdd: number,
    metadata?: { taskName?: string; boardName?: string; estimateHours?: number }
  ) => {
    if (secondsToAdd <= 0) return;
    
    try {
      const response = await axios.post(`${API_URL}/monday/task-time/${taskId}/add`, {
        secondsToAdd,
        taskName: metadata?.taskName,
        boardName: metadata?.boardName,
        estimateHours: metadata?.estimateHours,
        userId
      }, { withCredentials: true });
      
      if (!isMountedRef.current) return;
      
      const { totalSeconds, sessionCount } = response.data;
      
      // Update local cache
      setTimes(prev => ({
        ...prev,
        [taskId]: {
          ...prev[taskId],
          totalSeconds,
          sessionCount,
          lastSessionSeconds: secondsToAdd,
          taskName: metadata?.taskName || prev[taskId]?.taskName || null,
          boardName: metadata?.boardName || prev[taskId]?.boardName || null,
          estimateHours: metadata?.estimateHours || prev[taskId]?.estimateHours || null,
          updatedAt: new Date().toISOString()
        }
      }));
      
      // Ensure this task is marked as fetched
      fetchedTaskIdsRef.current.add(taskId);
    } catch (err: any) {
      console.error('Failed to add time to Monday task:', err);
      throw err;
    }
  }, [userId]);

  /**
   * Get time for a single task from cache
   */
  const getTime = useCallback((taskId: string): number => {
    return times[taskId]?.totalSeconds || 0;
  }, [times]);

  /**
   * Refresh all cached times
   */
  const refresh = useCallback(async () => {
    const taskIds = Array.from(fetchedTaskIdsRef.current);
    if (taskIds.length === 0) return;
    
    // Clear the fetched set so they get re-fetched
    fetchedTaskIdsRef.current.clear();
    await fetchTimes(taskIds);
  }, [fetchTimes]);

  return {
    times,
    loading,
    error,
    fetchTimes,
    addTime,
    getTime,
    refresh
  };
};

export default useMondayTaskTimes;

